import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useAuth } from '@/auth/AuthProvider';
import PageHeader from '@/components/PageHeader';
import PolandCompanyMap, { type PolandCompanyMapPoint } from '@/components/PolandCompanyMap';
import VariboxDetailsDialog from '@/components/VariboxDetailsDialog';
import VariboxStatusBadge from '@/components/VariboxStatusBadge';
import { useMovementDemoState } from '@/hooks/useMovementDemoState';
import { useI18n } from '@/i18n';
import { createLocation, getLocationTree, type ApiLocationNode } from '@/services/locationApi';
import { listVariboxes, mapApiVariboxToView, type VariboxViewRecord } from '@/services/variboxApi';

interface LocationOverviewStats {
  total: number;
  available: number;
  occupied: number;
  repair: number;
  blocked: number;
}

function collectNodeIds(node: ApiLocationNode): string[] {
  return [node.id, ...node.children.flatMap((child) => collectNodeIds(child))];
}

function findNodeById(nodes: ApiLocationNode[], id: string): ApiLocationNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }

    const inChildren = findNodeById(node.children, id);
    if (inChildren) {
      return inChildren;
    }
  }

  return null;
}

function computeOverviewStats(records: VariboxViewRecord[]): LocationOverviewStats {
  return records.reduce<LocationOverviewStats>((acc, record) => {
    acc.total += 1;

    if (record.status === 'available') {
      acc.available += 1;
    }

    if (record.status === 'occupied') {
      acc.occupied += 1;
    }

    if (record.status === 'repair' || record.status === 'damaged') {
      acc.repair += 1;
    }

    if (record.status === 'blocked') {
      acc.blocked += 1;
    }

    return acc;
  }, { total: 0, available: 0, occupied: 0, repair: 0, blocked: 0 });
}

function getNodeDisplay(node: ApiLocationNode) {
  const companyName = node.company_profile?.company_name?.trim();
  if ((node.code ?? '').toUpperCase() === 'PL99' && companyName) {
    return companyName;
  }

  if (node.code) {
    if (node.name && node.name !== node.code) {
      return `${node.code} - ${node.name}`;
    }

    return node.code;
  }

  return node.name;
}

function getNodeAddressDisplay(node: ApiLocationNode) {
  const addressLine = node.company_profile?.address_line?.trim();
  const postalCode = node.company_profile?.postal_code?.trim();
  const city = node.company_profile?.city?.trim();

  if (!addressLine && !postalCode && !city) {
    return '';
  }

  const postalCity = [postalCode, city].filter(Boolean).join(' ');
  return [addressLine, postalCity].filter(Boolean).join(', ');
}

function collectNodes(root: ApiLocationNode): ApiLocationNode[] {
  return [root, ...root.children.flatMap((child) => collectNodes(child))];
}

export default function LocationsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { movementHistory } = useMovementDemoState();

  const [tree, setTree] = useState<ApiLocationNode[]>([]);
  const [allVariboxes, setAllVariboxes] = useState<VariboxViewRecord[]>([]);
  const [openRoots, setOpenRoots] = useState<Record<string, boolean>>({});
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<VariboxViewRecord | null>(null);
  const [mobileRootId, setMobileRootId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [subLocationParentId, setSubLocationParentId] = useState('');
  const [subLocationName, setSubLocationName] = useState('');
  const [subLocationCode, setSubLocationCode] = useState('');
  const [subLocationKind, setSubLocationKind] = useState<'sublocation' | 'client_node'>('sublocation');
  const [companyAddressLine, setCompanyAddressLine] = useState('');
  const [companyPostalCode, setCompanyPostalCode] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [companyCityCode, setCompanyCityCode] = useState('');
  const [subLocationSaving, setSubLocationSaving] = useState(false);
  const [subLocationError, setSubLocationError] = useState('');
  const [subLocationSuccess, setSubLocationSuccess] = useState('');
  const [mapPoints, setMapPoints] = useState<PolandCompanyMapPoint[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedMapPointId, setSelectedMapPointId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const [locationResponse, variboxResponse] = await Promise.all([
          getLocationTree(),
          listVariboxes({ limit: 200, offset: 0 }),
        ]);

        if (cancelled) {
          return;
        }

        setTree(locationResponse.items);
        setAllVariboxes(variboxResponse.items.map(mapApiVariboxToView));
        setOpenRoots(locationResponse.items.reduce<Record<string, boolean>>((acc, item) => {
          acc[item.id] = true;
          return acc;
        }, {}));
      } catch {
        if (!cancelled) {
          setErrorMessage(t('locations.apiError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const companyProfiles = useMemo(() => {
    const companyRoots = tree.filter((node) => ['PL01', 'PL99'].includes((node.code ?? '').toUpperCase()));
    return companyRoots.flatMap((root) => collectNodes(root)
      .filter((node) => node.company_profile && (node.company_profile.city || node.company_profile.address_line))
      .map((node) => ({
        id: node.id,
        kind: node.kind,
        locationLabel: getNodeDisplay(node),
        companyName: node.company_profile?.company_name ?? node.name,
        addressLine: node.company_profile?.address_line ?? '',
        postalCode: node.company_profile?.postal_code ?? '',
        city: node.company_profile?.city ?? '',
        cityCode: node.company_profile?.city_code ?? '',
        })));
  }, [tree]);

  useEffect(() => {
    let cancelled = false;

    const loadMapPoints = async () => {
      if (companyProfiles.length === 0) {
        setMapPoints([]);
        setMapLoading(false);
        return;
      }

      setMapLoading(true);

      const results: PolandCompanyMapPoint[] = [];
      let lastRequestAt = 0;

      for (const profile of companyProfiles) {
        const postalCode = profile.postalCode.replace(/^(\d{2})-?(\d{3})$/, '$1-$2');
        const queries = [
          [profile.addressLine, postalCode, profile.city, 'Polska'].filter(Boolean).join(', '),
          [postalCode, profile.city, 'Polska'].filter(Boolean).join(', '),
        ].filter((query, index, all) => Boolean(query) && all.indexOf(query) === index);
        let point: PolandCompanyMapPoint | null = null;

        for (const [index, query] of queries.entries()) {
          const cacheKey = `assetflow.geo.v2.${query.toLowerCase()}`;
          const cached = window.sessionStorage.getItem(cacheKey);
          if (cached) {
            try {
              const parsed = JSON.parse(cached) as { lat: number; lon: number };
              point = { ...profile, lat: parsed.lat, lon: parsed.lon };
              break;
            } catch {
              window.sessionStorage.removeItem(cacheKey);
            }
          }

          if (lastRequestAt) {
            await new Promise((resolve) => window.setTimeout(resolve, Math.max(0, 1100 - (Date.now() - lastRequestAt))));
          }
          lastRequestAt = Date.now();

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=pl&limit=1&q=${encodeURIComponent(query)}`,
            );
            if (!response.ok) {
              continue;
            }
            const data = (await response.json()) as Array<{ lat: string; lon: string }>;
            const first = data[0];
            const lat = Number(first?.lat);
            const lon = Number(first?.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
              continue;
            }

            window.sessionStorage.setItem(cacheKey, JSON.stringify({ lat, lon }));
            point = { ...profile, lat, lon };
            break;
          } catch {
            if (index === queries.length - 1) {
              point = null;
            }
          }
        }

        if (point) {
          results.push(point);
        }
      }

      if (!cancelled) {
        setMapPoints(results);
        setMapLoading(false);
      }
    };

    loadMapPoints();

    return () => {
      cancelled = true;
    };
  }, [companyProfiles]);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId) {
      return null;
    }

    return findNodeById(tree, selectedNodeId);
  }, [selectedNodeId, tree]);

  const selectedMapNode = useMemo(() => {
    if (!selectedMapPointId) {
      return null;
    }

    return findNodeById(tree, selectedMapPointId);
  }, [selectedMapPointId, tree]);

  const selectedMapNodeStats = useMemo(() => {
    if (!selectedMapNode) {
      return { total: 0 };
    }

    const ids = new Set(collectNodeIds(selectedMapNode));
    const total = allVariboxes.filter((record) => record.currentLocationId && ids.has(record.currentLocationId)).length;
    return { total };
  }, [allVariboxes, selectedMapNode]);

  const selectedNodeIds = useMemo(() => {
    if (!selectedNode) {
      return [];
    }

    return collectNodeIds(selectedNode);
  }, [selectedNode]);

  const currentList = useMemo(() => {
    if (!selectedNode) {
      return [];
    }

    const locationSet = new Set(selectedNodeIds);
    return allVariboxes.filter((record) => {
      return record.currentLocationId ? locationSet.has(record.currentLocationId) : false;
    });
  }, [allVariboxes, selectedNode, selectedNodeIds]);

  const overviewStats = useMemo(() => computeOverviewStats(currentList), [currentList]);

  const selectedTitle = useMemo(() => {
    if (!selectedNode) {
      return t('locations.selectHint');
    }

    return getNodeDisplay(selectedNode);
  }, [selectedNode, t]);

  const rootLocationOptions = useMemo(() => {
    return tree.map((item) => ({ id: item.id, label: getNodeDisplay(item), code: item.code ?? '' }));
  }, [tree]);

  const selectedRoot = useMemo(() => {
    return rootLocationOptions.find((item) => item.id === subLocationParentId) ?? null;
  }, [rootLocationOptions, subLocationParentId]);

  const isCompanyRootSelected = selectedRoot?.code.toUpperCase() === 'PL01' || selectedRoot?.code.toUpperCase() === 'PL99';

  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    if (!rootLocationOptions.length) {
      return;
    }

    if (subLocationParentId) {
      const stillExists = rootLocationOptions.some((item) => item.id === subLocationParentId);
      if (stillExists) {
        return;
      }
    }

    const preferred = rootLocationOptions.find((item) => item.label.startsWith('PL01')) ?? rootLocationOptions[0];
    setSubLocationParentId(preferred.id);
  }, [rootLocationOptions, subLocationParentId]);

  const toggleRoot = (id: string) => {
    setOpenRoots((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  const navigateToInventory = () => {
    if (!selectedNode?.code) {
      navigate('/inventory');
      return;
    }

    if (selectedNode.code === 'PL01' || selectedNode.code === 'PL99') {
      navigate(`/inventory?code=${selectedNode.code}`);
      return;
    }

    navigate('/inventory');
  };

  const navigateToClientState = () => {
    if (!selectedMapNode) {
      return;
    }

    const params = new URLSearchParams();
    params.set('locationId', selectedMapNode.id);
    params.set('includeLocationDescendants', 'true');
    navigate(`/assets?${params.toString()}`);
  };

  const navigateToCompanyPoint = (point: PolandCompanyMapPoint) => {
    const params = new URLSearchParams({
      locationId: point.id,
      includeLocationDescendants: 'true',
    });
    navigate(`/assets?${params.toString()}`);
  };

  const activeRoot = useMemo(() => {
    if (!mobileRootId) {
      return null;
    }

    return tree.find((item) => item.id === mobileRootId) ?? null;
  }, [mobileRootId, tree]);

  const reload = () => {
    setSelectedNodeId(null);
    setMobileRootId(null);
    setErrorMessage('');
    setLoading(true);

    Promise.all([getLocationTree(), listVariboxes({ limit: 200, offset: 0 })])
      .then(([locationResponse, variboxResponse]) => {
        setTree(locationResponse.items);
        setAllVariboxes(variboxResponse.items.map(mapApiVariboxToView));
        setOpenRoots(locationResponse.items.reduce<Record<string, boolean>>((acc, item) => {
          acc[item.id] = true;
          return acc;
        }, {}));
      })
      .catch(() => {
        setErrorMessage(t('locations.apiError'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCreateSubLocation = async () => {
    if (!isAdmin || !subLocationParentId || !subLocationName.trim()) {
      return;
    }

    if (isCompanyRootSelected && (!companyAddressLine.trim() || !companyPostalCode.trim() || !companyCity.trim())) {
      setSubLocationError(t('locations.adminPanel.companyProfileRequiredHint'));
      return;
    }

    setSubLocationSaving(true);
    setSubLocationError('');
    setSubLocationSuccess('');

    try {
      await createLocation({
        parent_id: subLocationParentId,
        name: subLocationName.trim(),
        code: subLocationCode.trim() ? subLocationCode.trim() : null,
        kind: subLocationKind,
        active: true,
        company_name: isCompanyRootSelected ? subLocationName.trim() : null,
        address_line: isCompanyRootSelected ? (companyAddressLine.trim() || null) : null,
        postal_code: isCompanyRootSelected ? (companyPostalCode.trim() || null) : null,
        city: isCompanyRootSelected ? (companyCity.trim() || null) : null,
        city_code: isCompanyRootSelected ? (companyCityCode.trim() || null) : null,
      });
      setSubLocationName('');
      setSubLocationCode('');
      setSubLocationKind('sublocation');
      setCompanyAddressLine('');
      setCompanyPostalCode('');
      setCompanyCity('');
      setCompanyCityCode('');
      setSubLocationSuccess(t('locations.adminPanel.created'));
      reload();
    } catch (error) {
      let message = t('locations.adminPanel.createError');
      if (error instanceof AxiosError) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.trim()) {
          message = detail;
        }
      }
      setSubLocationError(message);
    } finally {
      setSubLocationSaving(false);
    }
  };

  if (isMobile) {
    return (
      <Stack spacing={2.5}>
        <PageHeader
          eyebrow={t('locations.eyebrow')}
          title={t('locations.mobile.title')}
          description={t('locations.mobile.description')}
          action={<Button variant="contained" size="large" onClick={() => navigate('/scan')}>{t('locations.actions.scanVaribox')}</Button>}
        />

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" fullWidth onClick={() => navigate('/scan')}>{t('locations.actions.scanVaribox')}</Button>
            <Button variant="outlined" fullWidth onClick={() => navigate('/assets')}>{t('locations.actions.searchVaribox')}</Button>
            <Button variant="outlined" fullWidth onClick={navigateToInventory}>{t('locations.actions.inventory')}</Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('locations.map.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('locations.map.description')}</Typography>
            <PolandCompanyMap
              points={mapPoints}
              loading={mapLoading}
              emptyLabel={mapLoading ? t('locations.map.loading') : t('locations.map.empty')}
              onPointSelect={(point) => {
                setSelectedMapPointId(point.id);
                setSelectedNodeId(point.id);
              }}
              onPointNavigate={navigateToCompanyPoint}
            />
            {selectedMapNode ? (
              <Button variant="contained" fullWidth onClick={navigateToClientState}>
                {t('locations.map.goToClientState')}
              </Button>
            ) : null}
          </Stack>
        </Paper>

        {isAdmin ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('locations.adminPanel.title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('locations.adminPanel.description')}</Typography>

              <TextField
                select
                fullWidth
                size="small"
                label={t('locations.adminPanel.parentLabel')}
                value={subLocationParentId}
                onChange={(event) => setSubLocationParentId(event.target.value)}
                disabled={subLocationSaving}
              >
                {rootLocationOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label={t('locations.adminPanel.kindLabel')}
                value={subLocationKind}
                onChange={(event) => setSubLocationKind(event.target.value as 'sublocation' | 'client_node')}
                disabled={subLocationSaving}
              >
                <MenuItem value="sublocation">{t('locations.adminPanel.kindSublocation')}</MenuItem>
                <MenuItem value="client_node">{t('locations.adminPanel.kindClientNode')}</MenuItem>
              </TextField>

              <TextField
                fullWidth
                size="small"
                label={t('locations.adminPanel.nameLabel')}
                value={subLocationName}
                onChange={(event) => setSubLocationName(event.target.value)}
                disabled={subLocationSaving}
              />

              <TextField
                fullWidth
                size="small"
                label={t('locations.adminPanel.codeLabel')}
                value={subLocationCode}
                onChange={(event) => setSubLocationCode(event.target.value)}
                disabled={subLocationSaving}
              />

              <Typography variant="caption" color="text.secondary">
                {isCompanyRootSelected ? t('locations.adminPanel.companyProfileHint') : t('locations.adminPanel.companyProfileOnlyHint')}
              </Typography>

              <TextField
                fullWidth
                size="small"
                label={t('locations.adminPanel.addressLabel')}
                value={companyAddressLine}
                onChange={(event) => setCompanyAddressLine(event.target.value)}
                disabled={subLocationSaving || !isCompanyRootSelected}
              />

              <TextField
                fullWidth
                size="small"
                label={t('locations.adminPanel.postalCodeLabel')}
                value={companyPostalCode}
                onChange={(event) => setCompanyPostalCode(event.target.value)}
                disabled={subLocationSaving || !isCompanyRootSelected}
              />

              <TextField
                fullWidth
                size="small"
                label={t('locations.adminPanel.cityLabel')}
                value={companyCity}
                onChange={(event) => setCompanyCity(event.target.value)}
                disabled={subLocationSaving || !isCompanyRootSelected}
              />

              <TextField
                fullWidth
                size="small"
                label={t('locations.adminPanel.cityCodeLabel')}
                value={companyCityCode}
                onChange={(event) => setCompanyCityCode(event.target.value)}
                disabled={subLocationSaving || !isCompanyRootSelected}
              />

              {subLocationError ? <Alert severity="error">{subLocationError}</Alert> : null}
              {subLocationSuccess ? <Alert severity="success">{subLocationSuccess}</Alert> : null}

              <Button
                variant="contained"
                disabled={subLocationSaving || !subLocationParentId || !subLocationName.trim()}
                onClick={handleCreateSubLocation}
              >
                {subLocationSaving ? t('locations.adminPanel.creating') : t('locations.adminPanel.createButton')}
              </Button>
            </Stack>
          </Paper>
        ) : null}

        {loading ? <Typography variant="body2" color="text.secondary">{t('locations.loadingData')}</Typography> : null}

        {!loading && errorMessage ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>{t('common.buttons.retry')}</Button>}>
            {errorMessage}
          </Alert>
        ) : null}

        {!loading && !errorMessage && !mobileRootId ? (
          <Stack spacing={1.25}>
            {tree.map((item) => (
              <Card key={item.id} variant="outlined">
                <CardContent>
                  <Stack spacing={1.25}>
                    <Typography variant="h6">{getNodeDisplay(item)}</Typography>
                    {getNodeAddressDisplay(item) ? (
                      <Typography variant="body2" color="text.secondary">{getNodeAddressDisplay(item)}</Typography>
                    ) : null}
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{item.varibox_count} {t('locations.mobile.variboxCount')}</Typography>
                    <Button variant="outlined" onClick={() => setMobileRootId(item.id)}>{t('locations.mobile.openLocation')}</Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : null}

        {!loading && !errorMessage && mobileRootId ? (
          <Stack spacing={1.25}>
            <Button variant="text" onClick={() => setMobileRootId(null)}>{t('locations.mobile.backToRoots')}</Button>
            <Typography variant="h6">{activeRoot ? getNodeDisplay(activeRoot) : t('locations.selectHint')}</Typography>

            {activeRoot?.children.map((child) => (
              <Card key={child.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.25}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{getNodeDisplay(child)}</Typography>
                      {getNodeAddressDisplay(child) ? (
                        <Typography variant="caption" color="text.secondary">{getNodeAddressDisplay(child)}</Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">{child.varibox_count} {t('locations.mobile.variboxCount')}</Typography>
                    </Box>
                    <Button variant="outlined" onClick={() => setSelectedNodeId(child.id)}>{t('locations.mobile.openList')}</Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}

            {selectedNode && activeRoot && selectedNode.id !== activeRoot.id ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedTitle}</Typography>
                  {currentList.length > 0 ? currentList.map((record) => (
                    <ListItemButton key={record.id} onClick={() => setSelectedRecord(record)} sx={{ borderRadius: 1.5, border: '1px solid rgba(148,163,184,0.2)', mb: 1 }}>
                      <ListItemText
                        primary={record.serialNumber}
                        secondary={`${record.radexId} · ${record.capacityL} L · ${record.lastMovement}`}
                      />
                    </ListItemButton>
                  )) : <Typography variant="body2" color="text.secondary">{t('locations.emptyNode')}</Typography>}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('locations.mobile.recentScans')}</Typography>
            {movementHistory.length > 0 ? movementHistory.slice(0, 3).map((entry) => (
              <Typography key={entry.id} variant="body2" color="text.secondary">
                {entry.variboxSerial} · {new Date(entry.happenedAtIso).toLocaleTimeString()} · {entry.destinationPlacement.locationCode}
              </Typography>
            )) : <Typography variant="body2" color="text.secondary">{t('locations.mobile.noRecentScans')}</Typography>}
          </Stack>
        </Paper>

        <VariboxDetailsDialog open={Boolean(selectedRecord)} record={selectedRecord} onClose={() => setSelectedRecord(null)} defaultHistoryVisible onUpdated={reload} />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t('locations.eyebrow')}
        title={t('locations.treeTitle')}
        description={t('locations.treeDescription')}
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => navigate('/scan')}>{t('locations.actions.scanVaribox')}</Button>
            <Button variant="outlined" onClick={() => navigate('/assets')}>{t('locations.actions.searchVaribox')}</Button>
            <Button variant="outlined" onClick={navigateToInventory}>{t('locations.actions.inventory')}</Button>
          </Stack>
        }
      />

      {isAdmin ? (
        <Paper variant="outlined" sx={{ p: 2.25 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('locations.adminPanel.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('locations.adminPanel.description')}</Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label={t('locations.adminPanel.parentLabel')}
                  value={subLocationParentId}
                  onChange={(event) => setSubLocationParentId(event.target.value)}
                  disabled={subLocationSaving}
                >
                  {rootLocationOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label={t('locations.adminPanel.kindLabel')}
                  value={subLocationKind}
                  onChange={(event) => setSubLocationKind(event.target.value as 'sublocation' | 'client_node')}
                  disabled={subLocationSaving}
                >
                  <MenuItem value="sublocation">{t('locations.adminPanel.kindSublocation')}</MenuItem>
                  <MenuItem value="client_node">{t('locations.adminPanel.kindClientNode')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('locations.adminPanel.nameLabel')}
                  value={subLocationName}
                  onChange={(event) => setSubLocationName(event.target.value)}
                  disabled={subLocationSaving}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('locations.adminPanel.codeLabel')}
                  value={subLocationCode}
                  onChange={(event) => setSubLocationCode(event.target.value)}
                  disabled={subLocationSaving}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {isCompanyRootSelected ? t('locations.adminPanel.companyProfileHint') : t('locations.adminPanel.companyProfileOnlyHint')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('locations.adminPanel.addressLabel')}
                  value={companyAddressLine}
                  onChange={(event) => setCompanyAddressLine(event.target.value)}
                  disabled={subLocationSaving || !isCompanyRootSelected}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('locations.adminPanel.postalCodeLabel')}
                  value={companyPostalCode}
                  onChange={(event) => setCompanyPostalCode(event.target.value)}
                  disabled={subLocationSaving || !isCompanyRootSelected}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label={t('locations.adminPanel.cityLabel')}
                  value={companyCity}
                  onChange={(event) => setCompanyCity(event.target.value)}
                  disabled={subLocationSaving || !isCompanyRootSelected}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('locations.adminPanel.cityCodeLabel')}
                  value={companyCityCode}
                  onChange={(event) => setCompanyCityCode(event.target.value)}
                  disabled={subLocationSaving || !isCompanyRootSelected}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="contained"
                  disabled={subLocationSaving || !subLocationParentId || !subLocationName.trim()}
                  onClick={handleCreateSubLocation}
                >
                  {subLocationSaving ? t('locations.adminPanel.creating') : t('locations.adminPanel.createButton')}
                </Button>
              </Grid>
            </Grid>

            {subLocationError ? <Alert severity="error">{subLocationError}</Alert> : null}
            {subLocationSuccess ? <Alert severity="success">{subLocationSuccess}</Alert> : null}
          </Stack>
        </Paper>
      ) : null}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('locations.map.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('locations.map.description')}</Typography>
            <PolandCompanyMap
              points={mapPoints}
              loading={mapLoading}
              emptyLabel={mapLoading ? t('locations.map.loading') : t('locations.map.empty')}
              onPointSelect={(point) => {
                setSelectedMapPointId(point.id);
                setSelectedNodeId(point.id);
              }}
              onPointNavigate={navigateToCompanyPoint}
            />

            {selectedMapNode ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {selectedMapNode.company_profile?.company_name || selectedMapNode.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('locations.map.selectedClientCount')}: {selectedMapNodeStats.total}
                    </Typography>
                  </Box>
                  <Button variant="contained" onClick={navigateToClientState}>
                    {t('locations.map.goToClientState')}
                  </Button>
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </Paper>

      {loading ? <Typography variant="body2" color="text.secondary">{t('locations.loadingData')}</Typography> : null}
      {!loading && errorMessage ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>{t('common.buttons.retry')}</Button>}>
          {errorMessage}
        </Alert>
      ) : null}

      {!loading && !errorMessage ? (
        <Grid container spacing={2.5}>
          <Grid item xs={12} xl={4} sx={{ minWidth: 0, display: 'flex' }}>
            <Paper variant="outlined" sx={{ p: 2.25, height: '100%', width: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('locations.treeSectionTitle')}</Typography>

                {tree.map((root) => (
                  <Box key={root.id}>
                    <List disablePadding>
                      <ListItemButton
                        onClick={() => {
                          setSelectedNodeId(root.id);
                          toggleRoot(root.id);
                        }}
                        selected={selectedNodeId === root.id}
                        sx={{ borderRadius: 1.5, border: '1px solid rgba(148,163,184,0.2)' }}
                      >
                        <ListItemText
                          primary={getNodeDisplay(root)}
                          secondary={getNodeAddressDisplay(root) || `${root.varibox_count} ${t('locations.countSuffix')}`}
                        />
                        <Chip label={openRoots[root.id] ? '−' : '+'} size="small" />
                      </ListItemButton>

                      {openRoots[root.id] ? root.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          sx={{ ml: 2, mt: 0.75, borderRadius: 1.5, border: '1px solid rgba(148,163,184,0.14)' }}
                          selected={selectedNodeId === child.id}
                          onClick={() => setSelectedNodeId(child.id)}
                        >
                          <ListItemText
                            primary={getNodeDisplay(child)}
                            secondary={getNodeAddressDisplay(child) || `${child.varibox_count} ${t('locations.countSuffix')}`}
                          />
                        </ListItemButton>
                      )) : null}
                    </List>

                    <Divider sx={{ my: 1.25 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} xl={8} sx={{ minWidth: 0, display: 'flex' }}>
            <Paper variant="outlined" sx={{ p: 2.25, height: '100%', width: '100%' }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedTitle}</Typography>

                {selectedNode ? (
                  <>
                    {getNodeAddressDisplay(selectedNode) ? (
                      <Typography variant="body2" color="text.secondary">{getNodeAddressDisplay(selectedNode)}</Typography>
                    ) : null}
                    <Grid container spacing={1.25}>
                      <Grid item xs={6} md={2.4}><Card variant="outlined" sx={{ height: '100%' }}><CardContent sx={{ minHeight: 82, '&:last-child': { pb: 2 } }}><Typography variant="caption" color="text.secondary" sx={{ minHeight: 30, display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>{t('locations.overview.total')}</Typography><Typography variant="h6" sx={{ lineHeight: 1.2 }}>{overviewStats.total}</Typography></CardContent></Card></Grid>
                      <Grid item xs={6} md={2.4}><Card variant="outlined" sx={{ height: '100%' }}><CardContent sx={{ minHeight: 82, '&:last-child': { pb: 2 } }}><Typography variant="caption" color="text.secondary" sx={{ minHeight: 30, display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>{t('locations.overview.available')}</Typography><Typography variant="h6" sx={{ lineHeight: 1.2 }}>{overviewStats.available}</Typography></CardContent></Card></Grid>
                      <Grid item xs={6} md={2.4}><Card variant="outlined" sx={{ height: '100%' }}><CardContent sx={{ minHeight: 82, '&:last-child': { pb: 2 } }}><Typography variant="caption" color="text.secondary" sx={{ minHeight: 30, display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>{t('locations.overview.occupied')}</Typography><Typography variant="h6" sx={{ lineHeight: 1.2 }}>{overviewStats.occupied}</Typography></CardContent></Card></Grid>
                      <Grid item xs={6} md={2.4}><Card variant="outlined" sx={{ height: '100%' }}><CardContent sx={{ minHeight: 82, '&:last-child': { pb: 2 } }}><Typography variant="caption" color="text.secondary" sx={{ minHeight: 30, display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>{t('locations.overview.repair')}</Typography><Typography variant="h6" sx={{ lineHeight: 1.2 }}>{overviewStats.repair}</Typography></CardContent></Card></Grid>
                      <Grid item xs={6} md={2.4}><Card variant="outlined" sx={{ height: '100%' }}><CardContent sx={{ minHeight: 82, '&:last-child': { pb: 2 } }}><Typography variant="caption" color="text.secondary" sx={{ minHeight: 30, display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>{t('locations.overview.blocked')}</Typography><Typography variant="h6" sx={{ lineHeight: 1.2 }}>{overviewStats.blocked}</Typography></CardContent></Card></Grid>
                    </Grid>

                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 760, tableLayout: 'auto' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('assets.headings.serialNumber')}</TableCell>
                            <TableCell>{t('assets.headings.radexId')}</TableCell>
                            <TableCell>{t('assets.headings.capacity')}</TableCell>
                            <TableCell>{t('assets.headings.status')}</TableCell>
                            <TableCell>{t('assets.headings.client')}</TableCell>
                            <TableCell>{t('assets.headings.rfid')}</TableCell>
                            <TableCell>{t('locations.lastMovement')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentList.map((record) => (
                            <TableRow key={record.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedRecord(record)}>
                              <TableCell>{record.serialNumber}</TableCell>
                              <TableCell>{record.radexId}</TableCell>
                              <TableCell>{record.capacityL} L</TableCell>
                              <TableCell><VariboxStatusBadge status={record.status} /></TableCell>
                              <TableCell>{record.client}</TableCell>
                              <TableCell>{record.rfid}</TableCell>
                              <TableCell>{record.lastMovement}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {currentList.length === 0 ? <Typography variant="body2" color="text.secondary">{t('locations.emptyNode')}</Typography> : null}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">{t('locations.selectHint')}</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      <VariboxDetailsDialog open={Boolean(selectedRecord)} record={selectedRecord} onClose={() => setSelectedRecord(null)} defaultHistoryVisible onUpdated={reload} />
    </Stack>
  );
}
